import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  IconButton,
  Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
  duration?: number; // Auto-close duration in ms, 0 for no auto-close
  actionText?: string;
  actionUrl?: string;
  onAction?: () => void;
  userId?: string; // To ensure notifications are user-specific
}

interface NotificationSystemProps {
  notifications: NotificationData[];
  onClose: (id: string) => void;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
}

const NotificationSystem: React.FC<NotificationSystemProps> = ({
  notifications,
  onClose,
  position = { vertical: 'bottom', horizontal: 'right' }
}) => {
  const navigate = useNavigate();
  const [visibleNotifications, setVisibleNotifications] = useState<string[]>([]);
  const [fadingNotifications, setFadingNotifications] = useState<string[]>([]);

  const handleClose = useCallback((id: string) => {
    // Start fade-out animation
    setFadingNotifications(prev => [...prev, id]);
    
    // Remove from visible after fade animation
    setTimeout(() => {
      setVisibleNotifications(prev => prev.filter(notifId => notifId !== id));
      setFadingNotifications(prev => prev.filter(notifId => notifId !== id));
      onClose(id);
    }, 500); // Wait for fade out animation
  }, [onClose]);

  useEffect(() => {
    // Show notifications with a slight delay for better UX
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    notifications.forEach((notification, index) => {
      const t = setTimeout(() => {
        setVisibleNotifications(prev => {
          if (!prev.includes(notification.id)) {
            return [...prev, notification.id];
          }
          return prev;
        });
      }, index * 200); // Stagger notifications
      timers.push(t);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [notifications]);

  useEffect(() => {
    // Auto-close notifications after duration (default 5 seconds)
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    notifications.forEach((notification) => {
      const duration = notification.duration ?? 5000;
      if (duration > 0) {
        timers.push(
          setTimeout(() => {
            handleClose(notification.id);
          }, duration)
        );
      }
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [handleClose, notifications]);

  const handleAction = (notification: NotificationData) => {
    if (notification.onAction) {
      notification.onAction();
    } else if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    handleClose(notification.id);
  };

  const getIcon = (type: NotificationData['type']) => {
    switch (type) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
      default:
        return <InfoIcon />;
    }
  };

  const getSeverity = (type: NotificationData['type']) => {
    return type;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: position.vertical === 'bottom' ? 24 : 'auto',
        top: position.vertical === 'top' ? 24 : 'auto',
        right: position.horizontal === 'right' ? 24 : 'auto',
        left: position.horizontal === 'left' ? 24 : 'auto',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 400,
        width: '100%',
        pointerEvents: 'none' // Allow clicks through container
      }}
    >
      {notifications.map((notification, index) => {
        const isVisible = visibleNotifications.includes(notification.id);
        const isFading = fadingNotifications.includes(notification.id);
        
        return (
          <Fade
            key={notification.id}
            in={isVisible && !isFading}
            timeout={{
              enter: 300,
              exit: 500
            }}
            style={{
              transformOrigin: 'bottom center'
            }}
          >
            <Box
              sx={{
                pointerEvents: 'auto', // Re-enable pointer events for notifications
                transform: `translateY(${isFading ? '20px' : '0px'})`,
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                opacity: isFading ? 0 : 1
              }}
            >
              <Alert
                severity={getSeverity(notification.type)}
                icon={getIcon(notification.type)}
                sx={{
                  width: '100%',
                  boxShadow: 3,
                  mb: 1,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
                action={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {notification.actionText && (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => handleAction(notification)}
                        sx={{ minWidth: 'auto' }}
                      >
                        {notification.actionText}
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={() => handleClose(notification.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              >
                <AlertTitle sx={{ mb: 0.5 }}>{notification.title}</AlertTitle>
                <Typography variant="body2">
                  {notification.message}
                </Typography>
              </Alert>
            </Box>
          </Fade>
        );
      })}
    </Box>
  );
};

export default NotificationSystem;